import api from './client';

export type PricingType = 'flat' | 'tiered' | 'custom';

/** One pricing tier, e.g. { name: 'Phone Consultation', price: 25 }. */
export interface ServiceTier {
  name: string;
  description?: string;
  price?: number | string | null;
}

export interface LegalService {
  id: number;
  name: string;
  description: string | null;
  price: string | number | null;
  pricingType: PricingType;
  estimatedTurnaround: string | null;
  tiers?: ServiceTier[] | null;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface LegalServiceInput {
  name: string;
  description?: string;
  price?: number | string | null;
  pricingType?: PricingType;
  estimatedTurnaround?: string;
  tiers?: ServiceTier[] | null;
  isActive?: boolean;
}

interface ListResponse {
  status: number;
  total: number;
  active: number;
  services: LegalService[];
}

interface MutationResponse {
  status: number;
  message?: string;
  error?: string | string[];
  service?: LegalService;
}

/** GET /api/services */
export const listLegalServices = async (
  active?: boolean
): Promise<{ services: LegalService[]; total: number; active: number }> => {
  const params: Record<string, string> = {};
  if (active === true) params.active = 'true';
  if (active === false) params.active = 'false';
  const res = await api.get<ListResponse>('/services', { params });
  return {
    services: res.data.services,
    total: res.data.total,
    active: res.data.active,
  };
};

/** GET /api/services/:id */
export const getLegalService = async (id: number): Promise<LegalService> => {
  const res = await api.get<{ status: number; service: LegalService }>(`/services/${id}`);
  return res.data.service;
};

/** POST /api/services (admin) */
export const createLegalService = async (
  payload: LegalServiceInput
): Promise<LegalService> => {
  const res = await api.post<MutationResponse>('/services', payload);
  if (!res.data.service) throw new Error('No service returned');
  return res.data.service;
};

/** PUT /api/services/:id (admin) */
export const updateLegalService = async (
  id: number,
  payload: LegalServiceInput
): Promise<LegalService> => {
  const res = await api.put<MutationResponse>(`/services/${id}`, payload);
  if (!res.data.service) throw new Error('No service returned');
  return res.data.service;
};

/** PATCH /api/services/:id/toggle (admin) */
export const toggleLegalService = async (
  id: number
): Promise<LegalService> => {
  const res = await api.patch<MutationResponse>(`/services/${id}/toggle`);
  if (!res.data.service) throw new Error('No service returned');
  return res.data.service;
};

/** DELETE /api/services/:id (admin) */
export const deleteLegalService = async (id: number): Promise<void> => {
  await api.delete<MutationResponse>(`/services/${id}`);
};

/** Pulls a human-readable error message out of an axios error. */
export const extractApiError = (err: unknown, fallback = 'Request failed'): string => {
  const e = err as { response?: { data?: { error?: string | string[]; message?: string } }; message?: string };
  const data = e?.response?.data;
  if (data?.error) {
    return Array.isArray(data.error) ? data.error.join(', ') : data.error;
  }
  if (data?.message) return data.message;
  if (e?.message) return e.message;
  return fallback;
};

/** Formats the backend's stringified DECIMAL into a display string like "$10.00". */
export const formatPrice = (
  price: LegalService['price'],
  pricingType?: PricingType
): string => {
  if (price === null || price === undefined || price === '') {
    return pricingType === 'custom' ? 'Custom' : '—';
  }
  const num = typeof price === 'number' ? price : Number(price);
  if (Number.isNaN(num)) return String(price);
  // Whole-dollar amounts: drop the .00.
  return num % 1 === 0 ? `$${num}` : `$${num.toFixed(2)}`;
};

/** "$25 – $50" from the cheapest to the dearest tier, one price if they're all equal. */
const tierPriceRange = (tiers: ServiceTier[]): string | null => {
  const prices = tiers
    .filter((t) => t.price !== null && t.price !== undefined && t.price !== '')
    .map((t) => Number(t.price))
    .filter((n) => Number.isFinite(n));
  if (prices.length === 0) return null;
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? formatPrice(min) : `${formatPrice(min)} – ${formatPrice(max)}`;
};

/**
 * The headline price for a service. Tiered services show their tier range
 * (min – max) instead of the base price; if no tier has a price, the base
 * price, if any. Null when there is nothing to show.
 */
export const servicePriceLabel = (
  service: Pick<LegalService, 'price' | 'pricingType' | 'tiers'>
): string | null => {
  if (service.pricingType === 'tiered') {
    const range = tierPriceRange(Array.isArray(service.tiers) ? service.tiers : []);
    if (range) return range;
    if (service.price === null || service.price === undefined || service.price === '') return null;
  }
  return formatPrice(service.price, service.pricingType);
};
